// src/pages/Caisses.js
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import Navbar from '../components/Navbar';
import { getActiveAuthRole, getActiveToken } from '../utils/authStorage';
import {
  FaPlus,
  FaToggleOn,
  FaToggleOff,
  FaDesktop,
  FaCircle,
  FaTimes,
  FaSpinner,
  FaSync,
  FaStore,
  FaBarcode,
  FaLink,
  FaEdit,
  FaTrash,
  FaChevronLeft,
  FaChevronRight,
  FaSearch,
  FaArrowLeft
} from 'react-icons/fa';

// Device service (inchangé)
const API_BASE = process.env.REACT_APP_DEVICE_SERVICE_URL || 'http://localhost:3001';
// Store service (comme dans Utilisateurs.js)
const STORE_API = process.env.REACT_APP_STORE_SERVICE_URL || 'http://localhost:3006';

const FILTER_FETCH_LIMIT = 3000;

const sortCaissesDevices = (list) =>
  list.slice().sort((a, b) => {
    const idA = String(a.clientId || a._id || '');
    const idB = String(b.clientId || b._id || '');
    const cmp = idA.localeCompare(idB, 'fr', { sensitivity: 'base', numeric: true });
    if (cmp !== 0) return cmp;
    return String(a.caisseName || '').localeCompare(String(b.caisseName || ''), 'fr', { sensitivity: 'base' });
  });

const deviceMatchesSearch = (dev, caisseNeedle, storeNeedle) => {
  const cn = String(caisseNeedle || '').trim().toLowerCase();
  const sc = String(storeNeedle || '').trim().toLowerCase();
  if (cn && !String(dev.caisseName || '').toLowerCase().includes(cn)) return false;
  if (sc && !String(dev.storeCode || '').toLowerCase().includes(sc)) return false;
  return true;
};

const formAccentBtnClass = 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg hover:from-blue-700 hover:to-blue-800 transition-all';
const formInputClass = 'w-full rounded-xl border border-slate-200/90 bg-slate-50/50 px-3.5 py-2.5 text-sm text-slate-800 shadow-[inset_0_1px_2px_rgba(15,23,42,0.03)] transition duration-200 placeholder:text-slate-400 hover:border-slate-300 hover:bg-white focus:border-blue-600 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-600/15';
const formLabelClass = 'mb-1.5 block text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400';

function DeviceFormDivider({ label }) {
  return (
    <div className="col-span-full flex items-center gap-3 pt-1">
      <span className="shrink-0 text-[10px] font-semibold uppercase tracking-[0.14em] text-blue-700">{label}</span>
      <div className="h-px flex-1 bg-slate-200" />
    </div>
  );
}

function DeviceFormField({ label, required, className = '', children }) {
  return (
    <div className={className}>
      <label className={formLabelClass}>
        {label}{required ? <span className="text-blue-600"> *</span> : ''}
      </label>
      {children}
    </div>
  );
}

function DeviceFormShell({
  title,
  subtitle,
  formId,
  onSubmit,
  onBack,
  onCancel,
  submitting,
  submitLabel,
  SubmitIcon,
  children,
}) {
  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="shrink-0 border-b bg-white shadow-sm">
        <div className="flex flex-col gap-3 p-3 sm:flex-row sm:items-center sm:justify-between sm:p-4 lg:px-6">
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gradient-to-r from-blue-600 to-blue-700">
              <FaDesktop className="text-sm text-white" />
            </div>
            <div className="min-w-0">
              <h1 className="truncate text-lg font-bold text-gray-900 sm:text-xl">{title}</h1>
              {subtitle && <p className="truncate text-xs text-gray-500">{subtitle}</p>}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button type="button" onClick={onBack} disabled={submitting} className="inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 sm:text-sm">
              <FaArrowLeft className="text-xs" />
              Retour
            </button>
            <button type="button" onClick={onCancel} disabled={submitting} className="rounded-lg border px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 sm:text-sm">
              Annuler
            </button>
            <button type="submit" form={formId} disabled={submitting} className={`inline-flex items-center rounded-lg px-3 py-2 text-xs font-medium disabled:opacity-50 sm:text-sm ${formAccentBtnClass}`}>
              {submitting ? <FaSpinner className="mr-1.5 animate-spin" /> : SubmitIcon ? <SubmitIcon className="mr-1.5 text-xs" /> : null}
              {submitLabel}
            </button>
          </div>
        </div>
      </div>

      <form id={formId} onSubmit={onSubmit} className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <div className="min-h-0 flex-1 overflow-hidden p-3 sm:p-4 lg:p-6">
          <div className="h-full rounded-xl border border-slate-200/80 bg-white p-5 shadow-sm ring-1 ring-slate-900/5 sm:p-6 lg:p-8">
            {children}
          </div>
        </div>
      </form>
    </div>
  );
}

export default function Caisses() {
  const navigate = useNavigate();
  const hideManagerDangerActions = ['manager', 'merchant'].includes(getActiveAuthRole());

  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [isUpdating, setIsUpdating] = useState(null);
  const [newDevice, setNewDevice] = useState({
    clientType: 'CAISSE',
    caisseName: '',
    storeCode: '',
    model: '',
    merchant: ''
  });

  // --- Liste des magasins ---
  const [stores, setStores] = useState([]);
  const [storesLoading, setStoresLoading] = useState(false);
  const [storesError, setStoresError] = useState(null);

  // Pagination UI (1-based)
  const [currentPage, setCurrentPage] = useState(1);
  const [totalDevices, setTotalDevices] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Détection si le serveur est 0-based (page=0 pour la première page)
  // null = inconnu, 1 = serveur 0-based, 0 = serveur 1-based
  const [serverPageOffset, setServerPageOffset] = useState(null);

  const [globalStats, setGlobalStats] = useState({ totalActive: 0, totalInactive: 0 });
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [editingDevice, setEditingDevice] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [editPayload, setEditPayload] = useState({ caisseName: '', storeCode: '' });

  const [draftCaisseName, setDraftCaisseName] = useState('');
  const [draftStoreCode, setDraftStoreCode] = useState('');
  const [appliedCaisseName, setAppliedCaisseName] = useState('');
  const [appliedStoreCode, setAppliedStoreCode] = useState('');
  const filteredAllRef = useRef([]);

  const itemsPerPage = 8;

  // --- Auth helpers (identique à Utilisateurs)
  const getToken = () => getActiveToken() || null;
  const authHeaders = () => {
    const token = getToken();
    const base = { 'Content-Type': 'application/json' };
    if (token) return { ...base, Authorization: `Bearer ${token}` };
    return base;
  };

  const handleAuthError = async (res) => {
    let body = null;
    try { body = await res.json(); } catch (e) { /* ignore */ }
    const msg = (body && body.message) ? body.message.toLowerCase() : '';
    if (res.status === 401 || msg.includes('token') || msg.includes('authorization') || msg.includes('non autorisé') || msg.includes('non autorise')) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      sessionStorage.removeItem('token');
      sessionStorage.removeItem('user');
      navigate('/login');
      throw new Error(body?.message || 'Authentification requise');
    }
    throw new Error(body?.message || `Erreur ${res.status}`);
  };

  // --- Fetch stores (magasins) — même façon que Utilisateurs.js
  const fetchStores = async () => {
    setStoresLoading(true);
    setStoresError(null);
    try {
      const headers = authHeaders();
      if (!headers) { navigate('/login'); return; }
      const res = await fetch(`${STORE_API.replace(/\/$/, '')}/stores`, { headers });
      if (!res.ok) await handleAuthError(res);
      const data = await res.json();
      // Support responses like: array, { stores: [...] }, { success: true, stores: [...]}
      const list = Array.isArray(data.stores) ? data.stores : (Array.isArray(data) ? data : (data.stores || []));
      setStores(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error('fetchStores error', err);
      setStores([]);
      setStoresError(err.message || String(err));
    } finally {
      setStoresLoading(false);
    }
  };

  // Fetch devices (pagination serveur sans filtre ; filtre nom/code → chargement large + pagination locale)
  const fetchDevices = (page = currentPage, explicitFilters = null) => {
    setLoading(true);
    setError(null);

    const requestedPage = Math.max(1, Number(page) || 1);

    const caisseFilter = explicitFilters ? String(explicitFilters.caisse ?? '') : appliedCaisseName;
    const storeFilter = explicitFilters ? String(explicitFilters.store ?? '') : appliedStoreCode;
    const filtersActive = !!(caisseFilter.trim() || storeFilter.trim());

    const headers = authHeaders();
    const fetchOptions = headers ? { headers } : { credentials: 'include', headers: { 'Content-Type': 'application/json' } };

    if (filtersActive) {
      let pageParamToSend;
      if (serverPageOffset === 1) {
        pageParamToSend = 0;
      } else {
        pageParamToSend = 1;
      }

      fetch(`${API_BASE}/devices?clientType=CAISSE&includePairing=true&page=${pageParamToSend}&limit=${FILTER_FETCH_LIMIT}&sortBy=clientId&sortOrder=asc`, fetchOptions)
        .then(async (res) => {
          const text = await res.text();
          try {
            const data = JSON.parse(text);
            if (!res.ok) throw new Error(data.message || data.error || text || 'Erreur récupération');
            return data;
          } catch (e) {
            if (!res.ok) throw new Error(text || 'Erreur récupération');
            throw e;
          }
        })
        .then((data) => {
          if (!data || !data.success) throw new Error(data && (data.message || 'Réponse invalide du serveur'));

          const list = Array.isArray(data.devices) ? data.devices : [];
          const sorted = sortCaissesDevices(list);
          const filtered = sorted.filter((d) => deviceMatchesSearch(d, caisseFilter, storeFilter));
          filteredAllRef.current = filtered;

          const total = filtered.length;
          const totalPg = total === 0 ? 1 : Math.ceil(total / itemsPerPage);
          const pageNum = Math.min(requestedPage, totalPg);
          const slice = filtered.slice((pageNum - 1) * itemsPerPage, pageNum * itemsPerPage);

          setDevices(slice);
          setTotalDevices(total);
          setTotalPages(totalPg);
          setCurrentPage(pageNum);

          fetchGlobalStats();
        })
        .catch((err) => setError(err.message || String(err)))
        .finally(() => setLoading(false));
      return;
    }

    filteredAllRef.current = [];

    let pageParamToSend;
    if (serverPageOffset === 1) {
      pageParamToSend = Math.max(0, requestedPage - 1);
    } else {
      pageParamToSend = Math.max(1, requestedPage);
    }

    fetch(`${API_BASE}/devices?clientType=CAISSE&includePairing=true&page=${pageParamToSend}&limit=${itemsPerPage}&sortBy=clientId&sortOrder=asc`, fetchOptions)
      .then(async res => {
        const text = await res.text();
        try {
          const data = JSON.parse(text);
          if (!res.ok) throw new Error(data.message || data.error || text || 'Erreur récupération');
          return data;
        } catch (e) {
          // si la réponse n'est pas JSON
          if (!res.ok) throw new Error(text || 'Erreur récupération');
          throw e;
        }
      })
      .then(data => {
        if (!data || !data.success) throw new Error(data && (data.message || 'Réponse invalide du serveur'));

        const list = Array.isArray(data.devices) ? data.devices : [];

        const sorted = sortCaissesDevices(list);

        // Détection du mode de pagination serveur
        const serverPageRaw = (data && (typeof data.page === 'number' || typeof data.page === 'string')) ? Number(data.page) : null;
        let effectiveServerOffset = serverPageOffset;
        if (serverPageRaw !== null && serverPageOffset === null) {
          const detectedOffset = requestedPage - serverPageRaw;
          effectiveServerOffset = detectedOffset === 1 ? 1 : 0;
          setServerPageOffset(effectiveServerOffset);
        }

        let normalizedCurrentPage;
        if (serverPageRaw !== null) {
          const offsetToUse = effectiveServerOffset !== null ? effectiveServerOffset : 0;
          normalizedCurrentPage = serverPageRaw + offsetToUse;
        } else {
          normalizedCurrentPage = requestedPage;
        }

        setDevices(sorted);
        setTotalDevices(Number(data.total) || 0);
        setTotalPages(Number(data.totalPages) || 0);
        setCurrentPage(Number(normalizedCurrentPage) || requestedPage);

        // Stats
        fetchGlobalStats();
      })
      .catch(err => setError(err.message || String(err)))
      .finally(() => setLoading(false));
  };

  const handleApplySearch = () => {
    setAppliedCaisseName(draftCaisseName);
    setAppliedStoreCode(draftStoreCode);
    fetchDevices(1, { caisse: draftCaisseName, store: draftStoreCode });
  };

  const handleResetSearch = () => {
    setDraftCaisseName('');
    setDraftStoreCode('');
    setAppliedCaisseName('');
    setAppliedStoreCode('');
    filteredAllRef.current = [];
    fetchDevices(1, { caisse: '', store: '' });
  };

  // Récupération des statistiques globales (sans pairing pour alléger)
  const fetchGlobalStats = async () => {
    try {
      const headers = authHeaders();
      const fetchOptions = headers ? { headers } : { credentials: 'include', headers: { 'Content-Type': 'application/json' } };

      const res = await fetch(`${API_BASE}/devices?clientType=CAISSE&limit=1000`, fetchOptions);
      const text = await res.text();
      const data = text ? JSON.parse(text) : null;

      if (data && data.success && Array.isArray(data.devices)) {
        const allDevices = data.devices;
        const activeCount = allDevices.filter(d => d.status === 'active').length;
        const inactiveCount = allDevices.filter(d => d.status === 'inactive').length;
        setGlobalStats({ totalActive: activeCount, totalInactive: inactiveCount });
      }
    } catch (err) {
      console.error('Erreur récupération statistiques:', err);
      setGlobalStats({ totalActive: 0, totalInactive: 0 });
    }
  };

  useEffect(() => {
    fetchDevices(1);
    // fetch stores dès le mount pour avoir la liste prête
    fetchStores();
  }, []);

  // Toggle status (active/inactive)
  const toggleStatus = async (id, currentStatus) => {
    const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
    setIsUpdating(id);
    try {
      const headers = authHeaders();
      const fetchOptions = headers ? { method: 'PUT', headers } : { method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' } };

      const res = await fetch(`${API_BASE}/devices/${id}/status?status=${newStatus}`, fetchOptions);
      const text = await res.text();
      let result;
      try { result = JSON.parse(text); } catch { result = { message: text }; }

      if (!res.ok) throw new Error(result.message || 'Erreur mise à jour statut');

      setDevices(prev => prev.map(dev => (dev.clientId === id || dev._id === id) ? { ...dev, status: newStatus } : dev));

      if (appliedCaisseName.trim() || appliedStoreCode.trim()) {
        filteredAllRef.current = filteredAllRef.current.map((dev) =>
          (dev.clientId === id || dev._id === id) ? { ...dev, status: newStatus } : dev
        );
      }

      setGlobalStats(prev => ({
        totalActive: newStatus === 'active' ? (prev.totalActive || 0) + 1 : Math.max(0, (prev.totalActive || 0) - 1),
        totalInactive: newStatus === 'inactive' ? (prev.totalInactive || 0) + 1 : Math.max(0, (prev.totalInactive || 0) - 1)
      }));

    } catch (err) {
      alert(`Erreur : ${err.message}`);
    } finally {
      setIsUpdating(null);
    }
  };

  // Création d'une nouvelle caisse (inchangé sauf que storeCode vient du select)
  const handleCreate = async e => {
    e.preventDefault();
    try {
      const headers = authHeaders();
      const fetchOptions = headers ? { method: 'POST', headers, body: JSON.stringify(newDevice) } : { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(newDevice) };

      const res = await fetch(`${API_BASE}/devices`, fetchOptions);
      const text = await res.text();
      let result;
      try { result = JSON.parse(text); } catch { result = { message: text }; }

      if (!res.ok) throw new Error(result.message || 'Erreur création');

      const createdObj = result.device || result.data || result.created || result || {};

      const generatedId = createdObj.clientId || createdObj._id || (newDevice.caisseName && newDevice.storeCode ? `${newDevice.caisseName}-${newDevice.storeCode}` : `local-${Date.now()}`);
      const deviceToInsert = {
        clientId: createdObj.clientId || createdObj._id || generatedId,
        _id: createdObj._id || createdObj.clientId || generatedId,
        caisseName: createdObj.caisseName || newDevice.caisseName || '',
        storeCode: createdObj.storeCode || newDevice.storeCode || '',
        model: createdObj.model || newDevice.model || '',
        merchant: createdObj.merchant || newDevice.merchant || '',
        status: createdObj.status || 'active',
        createdAt: createdObj.createdAt || createdObj.created || new Date().toISOString(),
        lastSeen: createdObj.lastSeen || null,
        pairing: createdObj.pairing || null,
        ...createdObj
      };

      setNewDevice({ clientType: 'CAISSE', caisseName: '', storeCode: '', model: '', merchant: '' });
      setShowForm(false);

      const hasSearch = appliedCaisseName.trim() || appliedStoreCode.trim();
      if (!hasSearch) {
        setDevices((prev) => sortCaissesDevices([deviceToInsert, ...prev]).slice(0, itemsPerPage));
        setTotalDevices(prev => (Number(prev) || 0) + 1);
        setCurrentPage(1);
      } else {
        fetchDevices(1);
      }

      setGlobalStats(prev => ({
        totalActive: deviceToInsert.status === 'active' ? (prev.totalActive || 0) + 1 : (prev.totalActive || 0),
        totalInactive: deviceToInsert.status === 'inactive' ? (prev.totalInactive || 0) + 1 : (prev.totalInactive || 0)
      }));

      // Rafraîchir les stats serveur
      fetchGlobalStats();

    } catch (err) {
      alert(`Erreur création : ${err.message}`);
    }
  };

  const openEditModal = (device) => {
    setEditingDevice(device);
    setEditPayload({
      caisseName: String(device?.caisseName || '').trim(),
      storeCode: String(device?.storeCode || '').trim()
    });
    if (stores.length === 0) fetchStores();
    setEditModalOpen(true);
  };

  const openDeleteModal = (device) => {
    setEditingDevice(device);
    setDeleteModalOpen(true);
  };

  const closeActionModals = () => {
    if (editLoading || deleteLoading) return;
    setEditModalOpen(false);
    setDeleteModalOpen(false);
    setEditingDevice(null);
    setEditPayload({ caisseName: '', storeCode: '' });
  };

  const handleUpdateCaisse = async (e) => {
    e.preventDefault();
    if (!editingDevice || editLoading) return;

    const nextCaisseName = String(editPayload.caisseName || '').trim();
    const nextStoreCode = String(editPayload.storeCode || '').trim();
    if (!nextCaisseName || !nextStoreCode) {
      alert('Le nom de caisse et le code magasin sont obligatoires.');
      return;
    }

    setEditLoading(true);
    try {
      const headers = authHeaders();
      const body = JSON.stringify({ caisseName: nextCaisseName, storeCode: nextStoreCode });
      const fetchOptions = headers
        ? { method: 'PUT', headers, body }
        : { method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body };

      const sourceClientId = editingDevice.clientId || editingDevice._id;
      const res = await fetch(`${API_BASE}/devices/${encodeURIComponent(sourceClientId)}/store`, fetchOptions);
      const text = await res.text();
      let result;
      try { result = JSON.parse(text); } catch { result = { message: text }; }
      if (!res.ok) throw new Error(result.message || 'Erreur mise à jour caisse');

      setEditModalOpen(false);
      setEditingDevice(null);
      fetchDevices(currentPage);
    } catch (err) {
      alert(`Erreur modification : ${err.message}`);
    } finally {
      setEditLoading(false);
    }
  };

  const handleDeleteCaisse = async () => {
    if (!editingDevice || deleteLoading) return;
    setDeleteLoading(true);
    try {
      const headers = authHeaders();
      const fetchOptions = headers
        ? { method: 'DELETE', headers }
        : { method: 'DELETE', credentials: 'include', headers: { 'Content-Type': 'application/json' } };

      const sourceClientId = editingDevice.clientId || editingDevice._id;
      const res = await fetch(`${API_BASE}/devices/${encodeURIComponent(sourceClientId)}`, fetchOptions);
      const text = await res.text();
      let result;
      try { result = JSON.parse(text); } catch { result = { message: text }; }
      if (!res.ok) throw new Error(result.message || 'Erreur suppression caisse');

      setDeleteModalOpen(false);
      setEditingDevice(null);
      fetchDevices(currentPage);
    } catch (err) {
      alert(`Erreur suppression : ${err.message}`);
    } finally {
      setDeleteLoading(false);
    }
  };

  // ... (le reste de ton composant DeviceCard, pagination etc. reste inchangé)
  // Je conserve tout ton JSX mais remplace la partie 'Code magasin' du formulaire par un select.

  const getStatusBadge = (status) => {
    const common = 'inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ';
    return status === 'active' ? (
      <span className={common + 'bg-emerald-50 text-emerald-700 ring-emerald-200/70'}>
        <FaCircle className="mr-1 h-2 w-2" />
        Actif
      </span>
    ) : (
      <span className={common + 'bg-rose-50 text-rose-700 ring-rose-200/70'}>
        <FaCircle className="mr-1 h-2 w-2" />
        Inactif
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '—';
    return date.toLocaleDateString('fr-FR', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
  };

  const formatCreationDate = (device) => {
    const creationDate = device.createdAt || device.created || device._createdAt || device.dateCreated || device.timestamp || device.creationDate;
    return formatDate(creationDate);
  };

  const DeviceCard = ({ device }) => (
    <div className="rounded-xl border border-slate-200/80 bg-white p-4 shadow-sm ring-1 ring-slate-900/5 transition hover:shadow-md">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
            <FaDesktop className="h-4 w-4" />
          </div>
          <div className="min-w-0">
            <div className="truncate font-mono text-sm font-semibold text-slate-900">{device.clientId || device._id}</div>
            <div className="mt-0.5 truncate text-xs text-slate-500">{device.caisseName || 'Nom non défini'}</div>
          </div>
        </div>
        <div className="shrink-0">{getStatusBadge(device.status)}</div>
      </div>

      <div className="mb-3 grid grid-cols-2 gap-3 text-xs">
        <div>
          <span className="font-semibold uppercase tracking-wide text-slate-500">Code magasin</span>
          <div className="mt-1">{device.storeCode ? (
            <span className="inline-flex rounded-lg border border-slate-200 bg-slate-50 px-2 py-0.5 font-mono text-[11px] font-semibold text-slate-600">{device.storeCode}</span>
          ) : '—'}</div>
        </div>
        <div>
          <span className="font-semibold uppercase tracking-wide text-slate-500">Appairage</span>
          <div className="mt-1">{device.pairing && device.pairing.paired ? (
            <span className="inline-flex max-w-full items-center truncate rounded-full bg-violet-50 px-2 py-0.5 text-[11px] font-semibold text-violet-700 ring-1 ring-violet-200/70"><FaLink className="mr-1 shrink-0" />{device.pairing.pairedDevice.clientId}</span>
          ) : (<span className="text-slate-400">Non appairé</span>)}</div>
        </div>
      </div>

      <div className="flex flex-col gap-3 border-t border-slate-100 pt-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="text-xs text-slate-500">Créée le {formatCreationDate(device)}</div>
        <div className="flex items-center justify-end gap-1">
          <button
            type="button"
            onClick={() => openEditModal(device)}
            title="Modifier"
            aria-label="Modifier"
            className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-blue-50 text-blue-700 transition hover:bg-blue-100"
          >
            <FaEdit className="h-3.5 w-3.5" />
          </button>
          {!hideManagerDangerActions && (
            <>
              <button
                type="button"
                onClick={() => openDeleteModal(device)}
                title="Supprimer"
                aria-label="Supprimer"
                className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-rose-50 text-rose-700 transition hover:bg-rose-100"
              >
                <FaTrash className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                onClick={() => toggleStatus(device.clientId, device.status)}
                disabled={isUpdating === device.clientId}
                title={device.status === 'active' ? 'Désactiver' : 'Activer'}
                aria-label={device.status === 'active' ? 'Désactiver' : 'Activer'}
                className={`inline-flex h-8 w-8 items-center justify-center rounded-lg transition disabled:opacity-50 ${device.status === 'active' ? 'bg-amber-50 text-amber-700 hover:bg-amber-100' : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'}`}
              >
                {isUpdating === device.clientId ? <FaSpinner className="h-3.5 w-3.5 animate-spin" /> : device.status === 'active' ? <FaToggleOff className="h-3.5 w-3.5" /> : <FaToggleOn className="h-3.5 w-3.5" />}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );

  const filteredAndSortedDevices = devices;

  // Pagination helpers (UI)
  const safeCurrentPage = Math.max(1, Number(currentPage) || 1);
  const startIndex = (safeCurrentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, totalDevices);

  const goToPage = (page) => {
    const p = Math.max(1, Number(page) || 1);
    const filtersActive = !!(appliedCaisseName.trim() || appliedStoreCode.trim());
    if (filtersActive) {
      const all = filteredAllRef.current;
      const total = all.length;
      const totalPg = total === 0 ? 1 : Math.ceil(total / itemsPerPage);
      if (p < 1 || p > totalPg || p === currentPage) return;
      const slice = all.slice((p - 1) * itemsPerPage, p * itemsPerPage);
      setDevices(slice);
      setCurrentPage(p);
      return;
    }
    if (p >= 1 && p <= totalPages && p !== currentPage) fetchDevices(p);
  };
  const formatDateSimple = (timestamp) => {
    if (!timestamp) return '—';
    // Parse ISO string directly without timezone conversion
    const match = String(timestamp).match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/);
    if (!match) return '—';
    const [, year, month, day, hour, minute] = match;
    return `${day}/${month}/${year} ${hour}:${minute}`;
  };
  const PaginationComponent = () => {
    if (totalPages <= 1) return null;
    const maxVisible = window.innerWidth < 768 ? 3 : 5;
    let start = Math.max(1, safeCurrentPage - Math.floor(maxVisible / 2));
    let end = Math.min(totalPages, start + maxVisible - 1);
    if (end - start + 1 < maxVisible) start = Math.max(1, end - maxVisible + 1);
    const pages = Array.from({ length: end - start + 1 }, (_, i) => start + i);

    return (
      <div className="sticky bottom-0 z-20 flex flex-col items-center justify-between gap-2 border-t border-slate-200/90 bg-white/95 px-4 py-3 backdrop-blur-sm sm:flex-row">
        <div className="order-2 text-sm text-slate-500 sm:order-1">
          Affichage de {totalDevices === 0 ? 0 : startIndex + 1} à {totalDevices === 0 ? 0 : endIndex} sur {totalDevices}
          {(appliedCaisseName.trim() || appliedStoreCode.trim()) && (
            <span className="hidden sm:inline"> — recherche active (max. {FILTER_FETCH_LIMIT} caisses chargées)</span>
          )}
        </div>
        <div className="order-1 flex items-center gap-1 sm:order-2">
          <button onClick={() => goToPage(safeCurrentPage - 1)} disabled={safeCurrentPage === 1} className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-sm text-slate-600 transition hover:bg-slate-50 disabled:opacity-40"><FaChevronLeft className="text-xs" /></button>
          {pages.map(p => (
            <button key={p} onClick={() => goToPage(p)} className={`min-w-[2rem] rounded-lg border px-2.5 py-1.5 text-sm font-medium transition ${p === safeCurrentPage ? 'border-blue-600 bg-blue-600 text-white' : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'}`}>{p}</button>
          ))}
          <button onClick={() => goToPage(safeCurrentPage + 1)} disabled={safeCurrentPage === totalPages} className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-sm text-slate-600 transition hover:bg-slate-50 disabled:opacity-40"><FaChevronRight className="text-xs" /></button>
        </div>
      </div>
    );
  };

  const isFormView = showForm || editModalOpen;

  const closeCreateForm = () => setShowForm(false);

  const closeEditForm = () => {
    if (editLoading) return;
    setEditModalOpen(false);
    setEditingDevice(null);
    setEditPayload({ caisseName: '', storeCode: '' });
  };

  const renderStoreSelect = (value, onChange) => {
    if (storesLoading) {
      return (
        <div className="flex items-center gap-2 rounded-xl border border-slate-200/90 bg-slate-50/50 px-3.5 py-2.5 text-sm text-slate-500">
          <FaSpinner className="animate-spin text-blue-600" /> Chargement des magasins…
        </div>
      );
    }
    if (storesError) {
      return <div className="rounded-xl border border-red-200 bg-red-50 px-3.5 py-2.5 text-sm text-red-600">Erreur : {storesError}</div>;
    }
    if (stores.length > 0) {
      return (
        <select required value={value} onChange={onChange} className={formInputClass}>
          <option value="">— Sélectionner un magasin —</option>
          {stores.map(s => {
            const id = s._id || s.id || s.code;
            const label = `${s.code ? `${s.code} — ` : ''}${s.name || s.label || ''}`;
            return <option key={id} value={s.code}>{label}</option>;
          })}
        </select>
      );
    }
    return (
      <input
        required
        placeholder="Ex: STORE001"
        value={value}
        onChange={onChange}
        className={formInputClass}
      />
    );
  };

  return (
    <div className="flex h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <Sidebar />
      <div className="flex min-h-0 flex-1 flex-col min-w-0 overflow-hidden">
        <Navbar />

        {showForm && (
          <DeviceFormShell
            title="Nouvelle caisse"
            subtitle="Enregistrer une caisse et l'associer à un magasin"
            formId="device-create-form"
            onSubmit={handleCreate}
            onBack={closeCreateForm}
            onCancel={closeCreateForm}
            submitting={false}
            submitLabel="Créer"
            SubmitIcon={FaPlus}
          >
            <div className="grid grid-cols-1 gap-x-6 gap-y-5 sm:grid-cols-2 lg:grid-cols-4">
              <DeviceFormDivider label="Informations caisse" />
              <DeviceFormField label="Nom de la caisse" required className="sm:col-span-2">
                <input required placeholder="Ex: CAISSE01" value={newDevice.caisseName} onChange={e => setNewDevice({ ...newDevice, caisseName: e.target.value })} className={formInputClass} />
              </DeviceFormField>
              <DeviceFormField label="Code magasin" required className="sm:col-span-2">
                {renderStoreSelect(newDevice.storeCode, e => setNewDevice({ ...newDevice, storeCode: e.target.value }))}
              </DeviceFormField>

              <DeviceFormDivider label="Identifiant généré" />
              <div className="col-span-full rounded-xl border border-blue-100 bg-blue-50/60 px-4 py-3">
                <div className="flex items-center gap-2">
                  <FaBarcode className="shrink-0 text-blue-600" />
                  <span className="font-mono text-sm text-blue-800">
                    {newDevice.caisseName && newDevice.storeCode
                      ? `${newDevice.caisseName}-${newDevice.storeCode}`
                      : 'Remplissez les champs ci-dessus'}
                  </span>
                </div>
              </div>
            </div>
          </DeviceFormShell>
        )}

        {editModalOpen && (
          <DeviceFormShell
            title="Modifier la caisse"
            subtitle={editingDevice?.clientId || editingDevice?._id || 'Mettre à jour les informations'}
            formId="device-edit-form"
            onSubmit={handleUpdateCaisse}
            onBack={closeEditForm}
            onCancel={closeEditForm}
            submitting={editLoading}
            submitLabel="Enregistrer"
            SubmitIcon={FaEdit}
          >
            <div className="grid grid-cols-1 gap-x-6 gap-y-5 sm:grid-cols-2 lg:grid-cols-4">
              <DeviceFormDivider label="Informations caisse" />
              <DeviceFormField label="Caisse ID" className="sm:col-span-2">
                <input readOnly value={editingDevice?.clientId || editingDevice?._id || ''} className={`${formInputClass} cursor-not-allowed bg-slate-100 font-mono text-slate-600`} />
              </DeviceFormField>
              <DeviceFormField label="Nom de la caisse" required className="sm:col-span-2">
                <input required value={editPayload.caisseName} onChange={(e) => setEditPayload((prev) => ({ ...prev, caisseName: e.target.value }))} className={formInputClass} />
              </DeviceFormField>
              <DeviceFormField label="Code magasin" required className="sm:col-span-2 lg:col-span-4">
                {renderStoreSelect(editPayload.storeCode, (e) => setEditPayload((prev) => ({ ...prev, storeCode: e.target.value })))}
              </DeviceFormField>
            </div>
          </DeviceFormShell>
        )}

        {!isFormView && (
        <>
        <div className="bg-white border-b shadow-sm">
          <div className="p-3 sm:p-4 lg:p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 space-y-3 sm:space-y-0">
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg flex items-center justify-center"><FaDesktop className="text-white text-sm" /></div>
                <h1 className="text-lg sm:text-xl font-bold text-gray-900">Caisses</h1>
              </div>

              <div className="flex items-center space-x-2">
                <button onClick={() => { fetchDevices(currentPage); fetchStores(); }} disabled={loading} className="flex items-center px-3 py-2 border rounded-lg text-xs font-medium hover:bg-gray-50">
                  <FaSync className={`${loading ? 'animate-spin' : 'group-hover:rotate-180'} mr-1.5 text-xs`} />
                  <span className="hidden sm:inline">Actualiser</span>
                </button>
                <button onClick={() => { setShowForm(true); if (stores.length===0) fetchStores(); }} className="flex items-center px-3 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all text-xs font-medium shadow-lg hover:shadow-xl">
                  <FaPlus className="mr-1.5 text-xs" />
                  <span className="hidden sm:inline">Ajouter une caisse</span>
                  <span className="sm:hidden">Ajouter</span>
                </button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2 md:grid-cols-3">
              <div className="flex min-w-0 items-center gap-2 rounded-xl border border-slate-200/80 bg-gradient-to-br from-white to-slate-50/90 px-2.5 py-2 shadow-sm ring-1 ring-slate-900/5">
                <FaDesktop className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                <div className="min-w-0">
                  <p className="truncate text-[10px] font-semibold uppercase tracking-wide text-slate-500">Total</p>
                  <p className="text-sm font-bold tabular-nums tracking-tight text-slate-900">{totalDevices}</p>
                </div>
              </div>
              <div className="flex min-w-0 items-center gap-2 rounded-xl border border-emerald-200/70 bg-emerald-50/50 px-2.5 py-2 shadow-sm ring-1 ring-emerald-900/5">
                <FaCircle className="h-3.5 w-3.5 shrink-0 text-emerald-600" />
                <div className="min-w-0">
                  <p className="truncate text-[10px] font-semibold uppercase tracking-wide text-emerald-700/90">Actives</p>
                  <p className="text-sm font-bold tabular-nums tracking-tight text-emerald-900">{globalStats.totalActive}</p>
                </div>
              </div>
              <div className="col-span-2 flex min-w-0 items-center gap-2 rounded-xl border border-rose-200/70 bg-rose-50/50 px-2.5 py-2 shadow-sm ring-1 ring-rose-900/5 md:col-span-1">
                <FaCircle className="h-3.5 w-3.5 shrink-0 text-rose-600" />
                <div className="min-w-0">
                  <p className="truncate text-[10px] font-semibold uppercase tracking-wide text-rose-700/90">Inactives</p>
                  <p className="text-sm font-bold tabular-nums tracking-tight text-rose-900">{globalStats.totalInactive}</p>
                </div>
              </div>
            </div>

            <div className="mt-3 border-t border-slate-200/90 pt-3">
              <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500">Recherche</p>
              <div className="flex flex-wrap items-end gap-x-3 gap-y-2">
                <div className="min-w-[120px] flex-1 sm:max-w-[180px]">
                  <label className="text-[10px] font-medium uppercase tracking-wide text-slate-500">Nom de la caisse</label>
                  <div className="relative mt-0.5">
                    <FaSearch className="absolute left-2 top-1/2 h-3 w-3 -translate-y-1/2 text-slate-400" />
                    <input
                      type="text"
                      value={draftCaisseName}
                      onChange={(e) => setDraftCaisseName(e.target.value)}
                      placeholder="Fragment du nom…"
                      className="block w-full rounded-lg border border-slate-200 bg-white py-1.5 pl-7 pr-2 text-xs text-slate-800 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                    />
                  </div>
                </div>
                <div className="min-w-[100px] flex-1 sm:max-w-[140px]">
                  <label className="text-[10px] font-medium uppercase tracking-wide text-slate-500">Code magasin</label>
                  <input
                    type="text"
                    value={draftStoreCode}
                    onChange={(e) => setDraftStoreCode(e.target.value)}
                    placeholder="Ex. STORE01…"
                    className="mt-0.5 block w-full rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-xs text-slate-800 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                  />
                </div>
                <div className="flex flex-wrap gap-1.5">
                  <button
                    type="button"
                    onClick={handleApplySearch}
                    disabled={loading}
                    className="inline-flex items-center rounded-lg bg-blue-600 px-2.5 py-1.5 text-xs font-medium text-white shadow-sm transition hover:bg-blue-700 disabled:opacity-50"
                  >
                    Appliquer
                  </button>
                  <button
                    type="button"
                    onClick={handleResetSearch}
                    disabled={loading}
                    className="inline-flex items-center rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:opacity-50"
                  >
                    Réinitialiser
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {deleteModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={closeActionModals}>
            <div className="bg-white rounded-2xl p-4 sm:p-6 w-full max-w-md shadow-2xl" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg sm:text-xl font-bold text-gray-900">Supprimer la caisse</h2>
                <button onClick={closeActionModals} className="text-gray-400 hover:text-gray-600 p-2" disabled={deleteLoading}>
                  <FaTimes />
                </button>
              </div>
              <p className="text-sm text-gray-700 mb-5">
                Confirmez la suppression de la caisse <span className="font-semibold">{editingDevice?.clientId || editingDevice?._id}</span>.
              </p>
              <div className="flex justify-end gap-2">
                <button type="button" onClick={closeActionModals} disabled={deleteLoading} className="px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 text-sm">Annuler</button>
                <button type="button" onClick={handleDeleteCaisse} disabled={deleteLoading} className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 text-sm inline-flex items-center">
                  {deleteLoading ? <FaSpinner className="animate-spin mr-2" /> : <FaTrash className="mr-2" />}
                  Supprimer
                </button>
              </div>
            </div>
          </div>
        )}

        <main className="flex-1 overflow-hidden p-3 sm:p-4 lg:p-6">
          {loading && (<div className="flex h-64 items-center justify-center px-4"><div className="text-center"><div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-2 border-slate-200 border-t-blue-600"></div><p className="text-sm text-gray-600">Chargement des caisses...</p></div></div>)}

          {error && (<div className="rounded-xl border border-red-200 bg-red-50 p-4 text-center sm:p-6"><FaTimes className="mx-auto mb-2 text-2xl text-red-500" /><p className="text-sm font-medium text-red-700">Erreur : {error}</p></div>)}

          {!loading && !error && (
            <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-xl border border-slate-200/80 bg-white shadow-sm ring-1 ring-slate-900/5">
              <div className="hidden min-h-0 flex-1 lg:block">
                <div className="h-full overflow-auto">
                  <table className="min-w-full">
                    <thead className="sticky top-0 z-10 border-b border-slate-200 bg-slate-50">
                      <tr>
                        <th className="px-5 py-3.5 text-left text-[10px] font-semibold uppercase tracking-[0.12em] text-blue-700">Caisse ID</th>
                        <th className="px-4 py-3.5 text-left text-[10px] font-semibold uppercase tracking-[0.12em] text-blue-700">Nom caisse</th>
                        <th className="px-4 py-3.5 text-left text-[10px] font-semibold uppercase tracking-[0.12em] text-blue-700">Code magasin</th>
                        <th className="px-4 py-3.5 text-left text-[10px] font-semibold uppercase tracking-[0.12em] text-blue-700">Appairage</th>
                        <th className="px-4 py-3.5 text-left text-[10px] font-semibold uppercase tracking-[0.12em] text-blue-700">Statut</th>
                        <th className="px-4 py-3.5 text-left text-[10px] font-semibold uppercase tracking-[0.12em] text-blue-700">Dernière activité</th>
                        <th className="px-4 py-3.5 text-left text-[10px] font-semibold uppercase tracking-[0.12em] text-blue-700">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 bg-white">
                      {filteredAndSortedDevices.length === 0 ? (
                        <tr>
                          <td colSpan={7} className="px-6 py-14 text-center">
                            <div className="mx-auto flex max-w-sm flex-col items-center text-slate-500">
                              <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100">
                                <FaDesktop className="text-2xl text-slate-400" />
                              </div>
                              <p className="text-sm font-semibold text-slate-700">Aucune caisse trouvée</p>
                              <p className="mt-1 text-xs text-slate-500">
                                {(appliedCaisseName.trim() || appliedStoreCode.trim()) ? 'Modifiez vos critères de recherche.' : 'Commencez par ajouter une caisse.'}
                              </p>
                            </div>
                          </td>
                        </tr>
                      ) : (
                        filteredAndSortedDevices.map((dev, idx) => (
                          <tr key={dev.clientId || dev._id} className={`transition-colors hover:bg-blue-50/40 ${idx % 2 === 1 ? 'bg-slate-50/40' : ''}`}>
                            <td className="px-5 py-4 align-top">
                              <div className="flex min-w-0 items-start gap-3">
                                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
                                  <FaDesktop className="h-3.5 w-3.5" />
                                </div>
                                <div className="min-w-0">
                                  <div className="truncate font-mono text-sm font-semibold text-slate-900">{dev.clientId || dev._id}</div>
                                  <div className="mt-0.5 text-[11px] text-slate-500">Créée le {formatCreationDate(dev)}</div>
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-4 align-top">
                              <div className="flex max-w-[12rem] items-center gap-2">
                                <FaStore className="shrink-0 text-sm text-slate-400" />
                                <span className="truncate text-sm font-medium text-slate-900">{dev.caisseName || '—'}</span>
                              </div>
                            </td>
                            <td className="px-4 py-4 align-top whitespace-nowrap">
                              {dev.storeCode ? (
                                <span className="inline-flex rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1 font-mono text-xs font-semibold text-slate-700">{dev.storeCode}</span>
                              ) : (
                                <span className="text-sm text-slate-400">—</span>
                              )}
                            </td>
                            <td className="px-4 py-4 align-top whitespace-nowrap">
                              {dev.pairing && dev.pairing.paired ? (
                                <span className="inline-flex max-w-[10rem] items-center truncate rounded-full bg-violet-50 px-2.5 py-1 text-xs font-semibold text-violet-700 ring-1 ring-violet-200/70">
                                  <FaLink className="mr-1 shrink-0" />
                                  <span className="truncate">{dev.pairing.pairedDevice.clientId}</span>
                                </span>
                              ) : (
                                <span className="text-sm text-slate-400">Non appairé</span>
                              )}
                            </td>
                            <td className="px-4 py-4 align-top whitespace-nowrap">{getStatusBadge(dev.status)}</td>
                            <td className="px-4 py-4 align-top whitespace-nowrap text-sm tabular-nums text-slate-500">{formatDateSimple(dev.lastSeen)}</td>
                            <td className="px-4 py-4 align-top">
                              <div className="flex items-center gap-1">
                                <button
                                  type="button"
                                  onClick={() => openEditModal(dev)}
                                  title="Modifier"
                                  aria-label="Modifier"
                                  className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-blue-50 text-blue-700 transition hover:bg-blue-100"
                                >
                                  <FaEdit className="h-3.5 w-3.5" />
                                </button>
                                {!hideManagerDangerActions && (
                                  <>
                                    <button
                                      type="button"
                                      onClick={() => openDeleteModal(dev)}
                                      title="Supprimer"
                                      aria-label="Supprimer"
                                      className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-rose-50 text-rose-700 transition hover:bg-rose-100"
                                    >
                                      <FaTrash className="h-3.5 w-3.5" />
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => toggleStatus(dev.clientId, dev.status)}
                                      disabled={isUpdating === dev.clientId}
                                      title={dev.status === 'active' ? 'Désactiver' : 'Activer'}
                                      aria-label={dev.status === 'active' ? 'Désactiver' : 'Activer'}
                                      className={`inline-flex h-8 w-8 items-center justify-center rounded-lg transition disabled:opacity-50 ${dev.status === 'active' ? 'bg-amber-50 text-amber-700 hover:bg-amber-100' : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'}`}
                                    >
                                      {isUpdating === dev.clientId ? <FaSpinner className="h-3.5 w-3.5 animate-spin" /> : dev.status === 'active' ? <FaToggleOff className="h-3.5 w-3.5" /> : <FaToggleOn className="h-3.5 w-3.5" />}
                                    </button>
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="min-h-0 flex-1 overflow-auto p-4 lg:hidden">
                {filteredAndSortedDevices.length === 0 ? (
                  <div className="py-12 text-center">
                    <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100">
                      <FaDesktop className="text-2xl text-slate-400" />
                    </div>
                    <p className="text-sm font-semibold text-slate-700">Aucune caisse trouvée</p>
                  </div>
                ) : (
                  <div className="space-y-3">{filteredAndSortedDevices.map(dev => <DeviceCard key={dev.clientId || dev._id} device={dev} />)}</div>
                )}
              </div>

              <PaginationComponent />
            </div>
          )}
        </main>
        </>
        )}
      </div>
    </div>
  );
}
